import {
  Button,
  Form,
  FormAlert,
  FormField,
  FormGroup,
  FormLabel,
  Modal,
  ModalBody,
  ModalDialog,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@ax/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { FieldError, SubmitHandler, useForm } from 'react-hook-form';
import { ResetPasswordFormInputSchema } from './ResetPasswordFormInputSchema';
import { useResetPassword } from '@/hooks/api';
import { routerPaths } from '@ax/constants';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/NotesLayout';

interface PasswordFormInput {
  newPassword: string;
  confirmPassword?: string;
}

const ResetPassword = () => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [showModal, setShowModal] = useState(true);
  const handleClose = () => setShowModal(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordFormInput>({
    resolver: zodResolver(ResetPasswordFormInputSchema),
  });

  const { mutateAsync: resetPassword, isLoading, isError, error } = useResetPassword();

  const handleResetPasswordFormSubmit: SubmitHandler<PasswordFormInput> = async (data) => {
    try {
      await resetPassword({ newPassword: data?.newPassword });

      reset();
      setShowModal(false);
      navigate(routerPaths.root);
    } catch (error) {
      console.error(error);
    }
  };

  const handleResetPasswordClick = () => {
    handleSubmit(handleResetPasswordFormSubmit)().catch(console.error);
  };

  useEffect(() => {
    if (isError) {
      setErrorMessage(error?.response?.data?.message);
      reset();
    }
  }, [isError, error]);

  return (
    <Layout>
      <Modal onShow={showModal}>
        <ModalDialog>
          <ModalHeader
            onHide={handleClose}
            showCloseIcon={false}
          >
            <ModalTitle>Reset Password</ModalTitle>
          </ModalHeader>
          <ModalBody>
            <Form>
              <FormAlert message={errorMessage} />
              <FormGroup className="mb-[20px]">
                <FormLabel className="input__label">New Password</FormLabel>
                <FormField error={errors.newPassword as FieldError}>
                  <input
                    type="password"
                    className="input"
                    {...register('newPassword')}
                  />
                </FormField>
              </FormGroup>
              <FormGroup>
                <FormLabel className="input__label">Re-enter Password</FormLabel>
                <FormField error={errors.confirmPassword as FieldError}>
                  <input
                    type="password"
                    className="input"
                    {...register('confirmPassword')}
                  />
                </FormField>
              </FormGroup>
            </Form>
          </ModalBody>
          <ModalFooter>
            <Button
              className="h-[40px]"
              block
              variant="primary"
              onClick={handleResetPasswordClick}
            >
              {isLoading ? 'Processing...' : 'Reset Password'}
            </Button>
          </ModalFooter>
        </ModalDialog>
      </Modal>
    </Layout>
  );
};

export default ResetPassword;
